import { useAskInputSearchModes } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModes.js';
import { useAskInputPlaceholder } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputPlaceholder.js';
import { normalizeAskInputSearchModeControls } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { useHappyDispatch } from '#pplx/frontend/apps/ask/computer/branding/ComputerAnimatedLockupShared.js';
import { useComputerModeDecorativeGlow } from '#pplx/frontend/apps/ask/hooks/computer-hooks/useComputerModeDecorativeGlow.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { useHomeAskInputAutofocus } from '#pplx/frontend/apps/ask/page-components/home/internal/useHomeAskInputAutofocus.js';
import { useHomeModeShortcuts } from '#pplx/frontend/apps/ask/page-components/home/useHomeModeShortcuts.js';
import { usePreloadThreadPagesOnIntent } from '#pplx/frontend/apps/ask/page-components/thread-pages-preload/usePreloadThreadPagesOnIntent.js';
import { useHoverPlaceholder } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
/** Reuses Home's focus, navigation intent, shortcuts, and appearance policy for the Composer. */
export function useHomeComposerPageIntegration({ inputFocusRef, inputQuery, onSearchModeSelected, recessedFrame, selectedSearchMode, }) {
    // Home's bounded focus restoration owns autofocus, including active-control protection.
    const { initialConfiguredModelResolved } = useHomeAskInputAutofocus(inputFocusRef);
    const searchModeControls = useMemo(() => normalizeAskInputSearchModeControls({ enabled: initialConfiguredModelResolved }), [initialConfiguredModelResolved]);
    const { searchModeControllerProps } = useAskInputSearchModes({
        enabled: initialConfiguredModelResolved,
        onSearchModeSelected,
    });
    const { subscriptionTier } = useIsSubscribed();
    useHomeModeShortcuts({
        enabled: searchModeControllerProps.showSearchMode === true,
        searchEnabled: subscriptionTier !== 'basic' && subscriptionTier !== 'viewer',
        onSearchModeSelect: searchModeControllerProps.onSearchModeSelect,
    });
    const isComputer = selectedSearchMode === SearchMode.ASI;
    const { $t } = useIntl();
    const hoverPlaceholder = useHoverPlaceholder();
    const computerPlaceholder = isComputer
        ? $t({
            id: 'aa0072cd64',
            defaultMessage: 'Do anything…',
            description: 'Placeholder text for the homepage computer ask input field',
        })
        : undefined;
    const placeholder = useAskInputPlaceholder({
        // A hovered suggestion previews its action; otherwise Computer mode keeps its task
        // prompt instead of the generic search examples.
        override: hoverPlaceholder || computerPlaceholder,
    });
    useHappyDispatch(isComputer, inputQuery);
    const decorativeGlow = useComputerModeDecorativeGlow(selectedSearchMode);
    const { isIncognito } = useIsIncognito({ reason: 'home-composer' });
    const { onClick: preloadOnClick, onKeyDown } = usePreloadThreadPagesOnIntent(selectedSearchMode);
    const onClick = useCallback(() => {
        preloadOnClick();
    }, [preloadOnClick]);
    return useMemo(() => ({
        composerProps: {
            appearance: recessedFrame ? (isComputer ? 'framed-accent' : 'framed') : undefined,
            dashed: isIncognito,
            decorativeGlow: recessedFrame ? undefined : decorativeGlow,
            hasShadow: !isIncognito,
            placeholder: Array.isArray(placeholder) ? placeholder[0] : placeholder,
        },
        searchModeControls,
        onClick,
        onKeyDown,
    }), [
        decorativeGlow,
        isComputer,
        isIncognito,
        onClick,
        onKeyDown,
        placeholder,
        recessedFrame,
        searchModeControls,
    ]);
}
//# sourceMappingURL=useHomeComposerPageIntegration.js.map